import { useState } from "react";
import { motion } from "framer-motion";
import { EnvelopeSimple, WhatsappLogo, MapPin, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage, CONTACT } from "../i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", company: "", inquiry_type: "info", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      if (!BACKEND_URL) throw new Error("static");
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, company: form.company || null }),
      });
      if (!res.ok) throw new Error("failed");
      toast.success(t.contact.success);
      setForm({ name: "", email: "", company: "", inquiry_type: "info", message: "" });
    } catch {
      if (!BACKEND_URL) {
        window.location.href = `mailto:${CONTACT.email}?subject=${encodeURIComponent(form.inquiry_type)}&body=${encodeURIComponent(form.message + "\n\n— " + form.name + " (" + form.email + ")")}`;
      } else {
        toast.error(t.contact.error);
      }
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full rounded-lg border border-white/10 bg-ink-surface px-4 py-3 font-mono text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-brand-cyan focus:ring-2 focus:ring-brand-cyan/30 transition-[border-color,box-shadow] duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 md:py-40 overflow-hidden bg-ink-soft spotlight">
      <span className="absolute -top-8 right-4 md:right-10 font-display font-bold text-[26vw] md:text-[18rem] leading-none text-white/[0.03] select-none pointer-events-none" aria-hidden="true">
        04
      </span>
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="font-mono text-xs uppercase tracking-[0.3em] text-brand-cyan mb-6" data-testid="contact-chapter-label">
          {t.chapters.contact}
        </motion.p>

        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
          >
            <h2 className="font-display font-semibold tracking-tight text-4xl md:text-5xl leading-[1.05]" data-testid="contact-title">
              {t.contact.title}
            </h2>
            <p className="mt-6 font-mono text-sm text-white/50 leading-relaxed max-w-md" data-testid="contact-subtitle">
              {t.contact.sub}
            </p>

            <div className="mt-12 space-y-5" data-testid="contact-direct">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/30">{t.contact.direct}</p>
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="flex items-center gap-4 text-white/70 hover:text-brand-cyan transition-colors duration-300 group">
                <span className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center group-hover:border-brand-cyan/50 transition-colors duration-300">
                  <EnvelopeSimple size={20} weight="duotone" />
                </span>
                <span className="font-mono text-sm">{CONTACT.email}</span>
              </a>
              <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-4 text-white/70 hover:text-brand-green transition-colors duration-300 group">
                <span className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center group-hover:border-brand-green/50 transition-colors duration-300">
                  <WhatsappLogo size={20} weight="duotone" />
                </span>
                <span className="font-mono text-sm">WhatsApp {CONTACT.phone}</span>
              </a>
              <div className="flex items-center gap-4 text-white/70" data-testid="contact-location">
                <span className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center">
                  <MapPin size={20} weight="duotone" />
                </span>
                <span className="font-mono text-sm">{CONTACT.city}</span>
              </div>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            onSubmit={handleSubmit}
            className="lg:col-span-7 rounded-lg border border-white/5 bg-ink p-7 md:p-10 space-y-6"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="cf-name" className="block font-mono text-[11px] uppercase tracking-widest text-white/40 mb-2">{t.contact.labels.name}</label>
                <input id="cf-name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder={t.contact.placeholders.name} className={inputCls} />
              </div>
              <div>
                <label htmlFor="cf-email" className="block font-mono text-[11px] uppercase tracking-widest text-white/40 mb-2">{t.contact.labels.email}</label>
                <input id="cf-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} placeholder={t.contact.placeholders.email} className={inputCls} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="cf-company" className="block font-mono text-[11px] uppercase tracking-widest text-white/40 mb-2">{t.contact.labels.company}</label>
                <input id="cf-company" data-testid="contact-company-input" value={form.company} onChange={set("company")} placeholder={t.contact.placeholders.company} className={inputCls} />
              </div>
              <div>
                <label htmlFor="cf-type" className="block font-mono text-[11px] uppercase tracking-widest text-white/40 mb-2">{t.contact.labels.type}</label>
                <select id="cf-type" data-testid="contact-type-select" value={form.inquiry_type} onChange={set("inquiry_type")} className={inputCls}>
                  {Object.entries(t.contact.types).map(([k, v]) => (
                    <option key={k} value={k} className="bg-ink">{v}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="cf-message" className="block font-mono text-[11px] uppercase tracking-widest text-white/40 mb-2">{t.contact.labels.message}</label>
              <textarea id="cf-message" data-testid="contact-message-textarea" required minLength={10} rows={5} value={form.message} onChange={set("message")} placeholder={t.contact.placeholders.message} className={`${inputCls} resize-none`} />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="group inline-flex items-center gap-3 rounded-full bg-brand-cyan text-ink px-8 py-4 font-mono text-xs uppercase tracking-widest font-bold hover:bg-brand-green disabled:opacity-50 transition-colors duration-300"
            >
              {sending ? t.contact.sending : t.contact.submit}
              <PaperPlaneTilt size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
